import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function EducationPage() {
  const [scrolled, setScrolled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const categories = [
    { id: 'all', name: 'All Topics', icon: 'ri-grid-line' },
    { id: 'longevity', name: 'Longevity', icon: 'ri-heart-pulse-line' },
    { id: 'hormones', name: 'Hormone Health', icon: 'ri-test-tube-line' },
    { id: 'nutrition', name: 'Nutrition', icon: 'ri-restaurant-line' },
    { id: 'fitness', name: 'Fitness', icon: 'ri-run-line' },
    { id: 'prevention', name: 'Prevention', icon: 'ri-shield-check-line' },
    { id: 'mental', name: 'Mental Health', icon: 'ri-mental-health-line' }
  ];

  const articles = [
    {
      id: 1,
      category: 'longevity',
      title: 'Understanding Longevity Medicine: The Science of Healthy Aging',
      excerpt: 'Discover how longevity medicine combines cutting-edge science with personalized care to optimize healthspan and extend quality of life.',
      image: 'https://public.readdy.ai/ai/img_res/b2f88bcb3c6ed6ada76f685ad6f4f47c.jpg',
      readTime: '8 min read',
      date: 'March 15, 2025'
    },
    {
      id: 2,
      category: 'hormones',
      title: 'Hormone Optimization: Beyond Traditional Replacement Therapy',
      excerpt: 'Learn how bioidentical hormone therapy and peptide treatments can restore vitality, improve cognitive function, and enhance overall wellness.',
      image: 'https://readdy.ai/api/search-image?query=elegant%20medical%20laboratory%20with%20pristine%20white%20surfaces%20showcasing%20advanced%20hormone%20testing%20equipment%20and%20bioidentical%20therapy%20vials%20arranged%20professionally%20in%20modern%20healthcare%20facility%20with%20soft%20natural%20lighting%20and%20clean%20minimalist%20aesthetic%20representing%20cutting%20edge%20endocrinology&width=800&height=500&seq=eduopt2&orientation=landscape',
      readTime: '10 min read',
      date: 'March 12, 2025'
    },
    {
      id: 3,
      category: 'prevention',
      title: 'Advanced Cardiac Screening: AI-Powered Early Detection',
      excerpt: 'Explore how artificial intelligence and advanced imaging are revolutionizing cardiovascular disease prevention and early intervention.',
      image: 'https://readdy.ai/api/search-image?query=state%20of%20the%20art%20cardiac%20CT%20scanner%20in%20pristine%20modern%20hospital%20imaging%20suite%20with%20sleek%20white%20and%20metallic%20surfaces%20featuring%20advanced%20artificial%20intelligence%20displays%20and%20holographic%20heart%20visualization%20technology%20representing%20premium%20preventive%20cardiology%20care&width=800&height=500&seq=eduopt3&orientation=landscape',
      readTime: '7 min read',
      date: 'March 10, 2025'
    },
    {
      id: 4,
      category: 'nutrition',
      title: 'Personalized Nutrition: Optimizing Diet for Your Unique Biology',
      excerpt: 'Understand how genetic testing and metabolic analysis can create a nutrition plan tailored specifically to your body\'s needs.',
      image: 'https://readdy.ai/api/search-image?query=beautifully%20arranged%20gourmet%20healthy%20meal%20featuring%20vibrant%20colorful%20organic%20vegetables%20fresh%20salmon%20lean%20proteins%20and%20nutrient%20rich%20superfoods%20on%20elegant%20white%20marble%20surface%20with%20soft%20natural%20lighting%20representing%20personalized%20executive%20nutrition%20and%20wellness%20optimization&width=800&height=500&seq=eduopt4&orientation=landscape',
      readTime: '6 min read',
      date: 'March 8, 2025'
    },
    {
      id: 5,
      category: 'fitness',
      title: 'Executive Fitness: Strength Training for Longevity',
      excerpt: 'Discover evidence-based strength training protocols designed to maintain muscle mass, bone density, and metabolic health as you age.',
      image: 'https://readdy.ai/api/search-image?query=confident%20professional%20executive%20in%20athletic%20wear%20performing%20strength%20training%20exercises%20with%20dumbbells%20in%20luxurious%20modern%20private%20gym%20facility%20featuring%20floor%20to%20ceiling%20windows%20natural%20light%20sleek%20equipment%20and%20minimalist%20design%20representing%20elite%20fitness%20and%20longevity%20optimization&width=800&height=500&seq=eduopt5&orientation=landscape',
      readTime: '9 min read',
      date: 'March 5, 2025'
    },
    {
      id: 6,
      category: 'mental',
      title: 'Stress Management for High Performers',
      excerpt: 'Learn practical strategies to manage stress, optimize cognitive performance, and maintain mental wellness in demanding executive roles.',
      image: 'https://readdy.ai/api/search-image?query=serene%20executive%20professional%20meditating%20in%20peaceful%20modern%20office%20space%20with%20abundant%20natural%20light%20minimalist%20zen%20design%20featuring%20indoor%20plants%20soft%20neutral%20tones%20and%20calming%20atmosphere%20representing%20mental%20wellness%20mindfulness%20and%20stress%20management%20for%20high%20achieving%20professionals&width=800&height=500&seq=eduopt6&orientation=landscape',
      readTime: '7 min read',
      date: 'March 3, 2025'
    },
    {
      id: 7,
      category: 'longevity',
      title: 'Peptide Therapy: The Future of Regenerative Medicine',
      excerpt: 'Explore how peptide therapy is transforming regenerative medicine with targeted treatments for healing, recovery, and cellular optimization.',
      image: 'https://readdy.ai/api/search-image?query=cutting%20edge%20biotechnology%20laboratory%20with%20pristine%20white%20surfaces%20showcasing%20peptide%20therapy%20research%20featuring%20elegant%20glass%20vials%20molecular%20structure%20displays%20and%20advanced%20regenerative%20medicine%20equipment%20in%20sophisticated%20modern%20medical%20facility%20with%20soft%20professional%20lighting&width=800&height=500&seq=eduopt7&orientation=landscape',
      readTime: '11 min read',
      date: 'March 1, 2025'
    },
    {
      id: 8,
      category: 'prevention',
      title: 'Comprehensive Health Screening: What Tests You Really Need',
      excerpt: 'Navigate the world of preventive testing with a guide to essential biomarkers, genetic panels, and advanced diagnostics.',
      image: 'https://readdy.ai/api/search-image?query=modern%20medical%20diagnostic%20center%20featuring%20advanced%20blood%20analysis%20equipment%20and%20comprehensive%20health%20screening%20technology%20in%20pristine%20white%20laboratory%20with%20professional%20medical%20staff%20in%20clean%20coats%20examining%20test%20results%20on%20digital%20displays%20representing%20premium%20preventive%20healthcare&width=800&height=500&seq=eduopt8&orientation=landscape',
      readTime: '8 min read',
      date: 'February 28, 2025'
    },
    {
      id: 9,
      category: 'hormones',
      title: 'Testosterone Optimization: Science-Based Approaches',
      excerpt: 'Understand the role of testosterone in male health, from muscle maintenance to cognitive function, and evidence-based optimization strategies.',
      image: 'https://readdy.ai/api/search-image?query=confident%20healthy%20athletic%20male%20executive%20in%20his%20prime%20displaying%20vitality%20and%20wellness%20in%20modern%20professional%20setting%20with%20natural%20lighting%20clean%20minimalist%20background%20representing%20optimal%20hormone%20health%20testosterone%20optimization%20and%20peak%20male%20performance&width=800&height=500&seq=eduopt9&orientation=landscape',
      readTime: '9 min read',
      date: 'February 25, 2025'
    }
  ];

  const filteredArticles = selectedCategory === 'all' 
    ? articles 
    : articles.filter(article => article.category === selectedCategory);

  const resources = [
    {
      title: 'Patient Portal Guide',
      description: 'Learn how to access your health records, test results, and communicate with Dr. Coradin',
      icon: 'ri-computer-line',
      link: '#portal-guide'
    },
    {
      title: 'Telemedicine Setup',
      description: 'Step-by-step instructions for your first virtual consultation',
      icon: 'ri-video-line',
      link: '#telemedicine'
    },
    {
      title: 'Lab Test Preparation',
      description: 'Guidelines for preparing for various diagnostic tests and panels',
      icon: 'ri-flask-line',
      link: '#lab-prep'
    },
    {
      title: 'Medication Management',
      description: 'Best practices for managing prescriptions and supplements',
      icon: 'ri-capsule-line',
      link: '#medications'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Page-level SEO */}
      <title>Health Education Center – Longevity, Hormone & Preventive Care Articles | CorexMed</title>
      <meta name="description" content="Explore CorexMed's Health Education Center for evidence-based articles on longevity medicine, hormone optimization, AI cardiac screening, personalized nutrition, executive fitness, and preventive care by Dr. David Coradin." />
      <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/education`} />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Health Education Center – CorexMed",
        "description": "Evidence-based health education articles on longevity medicine, hormone optimization, AI cardiac screening, personalized nutrition, and executive fitness.",
        "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/education`,
        "isPartOf": {
          "@type": "WebSite",
          "name": "CorexMed",
          "url": import.meta.env.VITE_SITE_URL ?? 'https://example.com'
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL ?? 'https://example.com' },
            { "@type": "ListItem", "position": 2, "name": "Education", "item": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/education` }
          ]
        }
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "CorexMed Health Education Articles",
        "description": "A curated collection of evidence-based health articles covering longevity medicine, hormone optimization, cardiac screening, nutrition, fitness, and mental health.",
        "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/education`,
        "numberOfItems": 9,
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Understanding Longevity Medicine: The Science of Healthy Aging", "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/education#longevity-medicine` },
          { "@type": "ListItem", "position": 2, "name": "Hormone Optimization: Beyond Traditional Replacement Therapy", "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/education#hormone-optimization` },
          { "@type": "ListItem", "position": 3, "name": "Advanced Cardiac Screening: AI-Powered Early Detection", "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/education#cardiac-screening` },
          { "@type": "ListItem", "position": 4, "name": "Personalized Nutrition: Optimizing Diet for Your Unique Biology", "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/education#personalized-nutrition` },
          { "@type": "ListItem", "position": 5, "name": "Executive Fitness: Strength Training for Longevity", "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/education#executive-fitness` },
          { "@type": "ListItem", "position": 6, "name": "Stress Management for High Performers", "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/education#stress-management` },
          { "@type": "ListItem", "position": 7, "name": "Peptide Therapy: The Future of Regenerative Medicine", "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/education#peptide-therapy` },
          { "@type": "ListItem", "position": 8, "name": "Comprehensive Health Screening: What Tests You Really Need", "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/education#health-screening` },
          { "@type": "ListItem", "position": 9, "name": "Testosterone Optimization: Science-Based Approaches", "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/education#testosterone-optimization` }
        ]
      })}</script>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              CorexMed
            </h1>
          </Link>
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/#services" className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors">Services</Link>
            <Link to="/#about" className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors">About Dr. Coradin</Link>
            <Link to="/#testing" className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors">Testing</Link>
            <Link to="/education" className="text-sm font-medium text-amber-600">Education</Link>
            <Link to="/signup" className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors">Sign Up</Link>
            <a href="#portal" className="px-5 py-2 rounded-full border-2 border-gray-900 text-gray-900 text-sm font-medium whitespace-nowrap hover:bg-gray-900 hover:text-white transition-all cursor-pointer">
              Patient Portal
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)' }}></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block px-4 py-2 bg-amber-600/20 border border-amber-600/30 text-amber-400 rounded-full text-xs font-semibold mb-6">
              HEALTH EDUCATION CENTER
            </div>
            <h1 className="text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Knowledge is the Foundation<br />
              <span className="text-amber-500">of Optimal Health</span>
            </h1>
            <p className="text-xl text-white/70 leading-relaxed">
              Explore evidence-based insights on longevity, preventive care, and cutting-edge medical advances from Dr. Coradin and the CorexMed team.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-gray-50 py-8 sticky top-20 z-40 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  selectedCategory === category.id
                    ? 'bg-amber-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <i className={`${category.icon} text-base`}></i>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article) => (
              <article
                key={article.id}
                className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all group cursor-pointer border border-gray-100"
              >
                <div className="w-full h-56 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
                      {categories.find(c => c.id === article.category)?.name}
                    </span>
                    <span className="text-xs text-gray-500">{article.readTime}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-amber-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{article.date}</span>
                    <a href="#read" className="inline-flex items-center gap-2 text-amber-600 font-semibold text-sm hover:gap-3 transition-all">
                      Read More <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Patient Resources */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Patient Resources
            </h2>
            <p className="text-lg text-gray-600">
              Essential guides to help you make the most of your CorexMed experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {resources.map((resource, index) => (
              <a
                key={index}
                href={resource.link}
                className="bg-white p-6 rounded-lg shadow-sm hover:shadow-lg transition-all group cursor-pointer"
              >
                <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-all">
                  <i className={`${resource.icon} text-2xl text-amber-700 group-hover:text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{resource.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                  {resource.description}
                </p>
                <span className="inline-flex items-center gap-2 text-amber-600 font-semibold text-sm group-hover:gap-3 transition-all">
                  Learn More <i className="ri-arrow-right-line"></i>
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 bg-gradient-to-br from-emerald-900 to-teal-800">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-mail-line text-3xl text-white"></i>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Stay Informed on Health Advances
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Receive monthly insights on longevity medicine, preventive care, and the latest in executive health optimization.
          </p>
          
          <form 
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            data-readdy-form 
            id="education-newsletter-form"
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const data = new URLSearchParams();
              formData.forEach((value, key) => {
                data.append(key, value.toString());
              });
              fetch('https://readdy.ai/api/form/d4mifgrifile0aqbfo40', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: data.toString()
              }).then(() => {
                const successMsg = document.createElement('div');
                successMsg.className = 'text-white text-sm mt-4';
                successMsg.textContent = 'Thank you for subscribing!';
                e.currentTarget.appendChild(successMsg);
                e.currentTarget.reset();
                setTimeout(() => successMsg.remove(), 3000);
              }).catch(() => {
                const errorMsg = document.createElement('div');
                errorMsg.className = 'text-red-300 text-sm mt-4';
                errorMsg.textContent = 'Subscription failed. Please try again.';
                e.currentTarget.appendChild(errorMsg);
                setTimeout(() => errorMsg.remove(), 3000);
              });
            }}
          >
            <input
              type="email"
              name="email"
              placeholder="Enter your email"
              required
              className="flex-1 px-6 py-4 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
            />
            <button
              type="submit"
              className="px-8 py-4 bg-white text-gray-900 rounded-full font-semibold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <Link to="/">
                <h4 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>CorexMed</h4>
              </Link>
              <p className="text-sm text-gray-400 leading-relaxed">
                Executive Medicine for Exceptional Lives
              </p>
            </div>
            
            <div>
              <h5 className="font-bold mb-4 text-base">Navigate</h5>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/#about" className="hover:text-amber-600 transition-colors">About Dr. Coradin</Link></li>
                <li><Link to="/#services" className="hover:text-amber-600 transition-colors">Services</Link></li>
                <li><Link to="/#testing" className="hover:text-amber-600 transition-colors">Testing & Diagnostics</Link></li>
                <li><Link to="/education" className="hover:text-amber-600 transition-colors">Education</Link></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-bold mb-4 text-base">For Patients</h5>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="#portal" className="hover:text-amber-600 transition-colors flex items-center gap-2 cursor-pointer">
                    <i className="ri-lock-line"></i> Portal Login
                  </a>
                </li>
                <li>
                  <a href="#schedule" className="hover:text-amber-600 transition-colors flex items-center gap-2 cursor-pointer">
                    <i className="ri-calendar-line"></i> Schedule Appointment
                  </a>
                </li>
                <li>
                  <a href="#telemedicine" className="hover:text-amber-600 transition-colors flex items-center gap-2 cursor-pointer">
                    <i className="ri-video-line"></i> Telemedicine Access
                  </a>
                </li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-bold mb-4 text-base">Contact</h5>
              <p className="text-sm text-gray-400 mb-2">12345 Executive Drive</p>
              <p className="text-sm text-gray-400 mb-4">Miramar, FL 33025</p>
              <div className="flex gap-3">
                <a href="#linkedin" className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-amber-600 transition-all cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
                <a href="#instagram" className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-amber-600 transition-all cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <p>&copy; 2025 CorexMed. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#privacy" className="hover:text-amber-600 transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#terms" className="hover:text-amber-600 transition-colors cursor-pointer">Terms of Service</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-amber-600 transition-colors cursor-pointer">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
